import React, { useState, useEffect, useCallback } from 'react'
import styles from './FileUploadWithOptions.module.css'
import { TEST_TYPES, TestType, errorMessages } from '../constants/Constants'
import {
  Typography,
  Box,
  Tooltip,
  Button,
  CircularProgress,
  Modal,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material'

import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteIcon from '@mui/icons-material/Delete'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import SaveIcon from '@mui/icons-material/Save'
import ZoomInOutlined from '@mui/icons-material/ZoomInOutlined'
import { toast } from 'react-toastify'
import { useAppSelector } from '../../../app/hooks'
import {
  uploadAllDocuments,
  getAllDocuments,
  uploadDocument,
  updateUploadAllDocuments,
  removeDocument,
} from '../../../services/CampManagementService'
// import {
//   UploadFile as UploadFileIcon,
//   Delete as DeleteIcon,
//   FileDownloadOutlined,
//   Save as SaveIcon,
//   ZoomIn as ZoomInOutlined,
// } from '@mui/icons-material'

export interface Document {
  docId: string
  title: string
  url: string
  file?: File
  preview?: string
  testType?: TestType
  fileId?: string
  isLoading?: boolean
}

interface FileUploadWithOptionsProps {
  patientUhid?: string
  visitId?: string
  muhid?: string
  onUploadComplete?: () => void
}

const FileUploadWithOptions: React.FC<FileUploadWithOptionsProps> = ({
  patientUhid,
  visitId,
  onUploadComplete,
}): JSX.Element => {
  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, Document[]>
  >({
    pft: [],
    ecg: [],
    pap: [],
  })

  const [selectedFile, setSelectedFile] = useState<string>('')
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [selectedOptions, setSelectedOptions] = useState({
    pft: false,
    ecg: false,
    pap: false,
  })

  const selectedCamp = useAppSelector(
    (state) => state?.campManagement?.selectedCamp
  )

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOptions({
      ...selectedOptions,
      [event?.target?.name]: event?.target?.checked,
    })
  }

  const handlePreview = (file: Document) => {
    if (file?.preview) {
      setSelectedFile(file?.preview)
      setIsLightboxOpen(true)
    } else if (file?.url) {
      setSelectedFile(file?.url)
      setIsLightboxOpen(true)
    }
  }

  const handleDeleteFile = async (docId: string, testType: TestType) => {
    if (!docId) return

    try {
      if (docId?.startsWith('temp-')) {
        setUploadedFiles((prevFiles) => ({
          ...prevFiles,
          [testType]: prevFiles[testType]?.filter(
            (file) => file?.docId !== docId
          ),
        }))
      } else {
        await removeDocument(docId)

        setUploadedFiles((prevFiles) => ({
          ...prevFiles,
          [testType]: prevFiles[testType]?.filter(
            (file) => file?.docId !== docId
          ),
        }))
      }

      toast.success('File deleted successfully')
    } catch (error) {
      toast.error('Failed to delete file. Please try again.')
    }
  }

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response?.blob()
      const downloadUrl = window?.URL?.createObjectURL(blob)
      const link = document?.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', filename)
      document?.body?.appendChild(link)
      link?.click()
      link?.remove()
      window?.URL?.revokeObjectURL(downloadUrl)
    } catch (error) {
      toast.error(errorMessages?.camp?.fileUpload?.error)
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    testType: TestType
  ) => {
    if (!event?.target?.files || event?.target?.files?.length === 0) return

    const files = Array.from(event?.target?.files)
    const testTypeLabel = errorMessages?.camp?.testTypeShortLabels[testType]

    try {
      // Create temporary files with loading state
      const tempFiles = files?.map((file) => ({
        docId: `temp-${Date.now()}-${Math?.random()
          ?.toString(36)
          ?.substr(2, 9)}`,
        title: file?.name,
        file,
        testType,
        preview: URL.createObjectURL(file),
        url: '',
        fileId: '',
        isLoading: true,
      }))

      // Add temporary files to state
      setUploadedFiles((prevFiles) => ({
        ...prevFiles,
        [testType]: [...(prevFiles[testType] || []), ...tempFiles],
      }))

      // Process uploads
      const uploadPromises = files?.map(async (file, index) => {
        return uploadDocument(file, {
          patientUhid,
          campId: selectedCamp?.campId,
          visitId,
          testTypeLabel,
          index,
        })
      })

      const uploadResults = await Promise.all(uploadPromises)

      // Update state with uploaded files
      setUploadedFiles((prevFiles) => {
        const updatedFiles = { ...prevFiles }

        uploadResults?.forEach((result, index) => {
          const tempFile = tempFiles[index]
          if (tempFile) {
            const updatedFile = {
              ...tempFile,
              url: result?.data?.url,
              fileId: '',
              title: result?.data?.documentName,
              isLoading: false,
            }

            updatedFiles[testType] = updatedFiles[testType]?.map((file) =>
              file?.docId === tempFile?.docId ? updatedFile : file
            )
          }
        })

        return updatedFiles
      })

      if (onUploadComplete) {
        onUploadComplete()
      }

      toast.success(
        errorMessages?.camp?.fileUpload?.success || 'File uploaded successfully'
      )
    } catch (error) {
      toast.error(
        errorMessages?.camp?.fileUpload?.error || 'Failed to upload file'
      )

      // Clean up temporary files on error
      setUploadedFiles((prevFiles) => ({
        ...prevFiles,
        [testType]: (prevFiles[testType] || [])?.filter(
          (file) => !file.isLoading && !file.url.includes('temp-')
        ),
      }))
    }
  }

  const handleSave = async () => {
    if (!patientUhid && !visitId) {
      toast.error(errorMessages?.camp?.patientUhidAndVisitIdRequired)
      return
    }
    if (!patientUhid) {
      toast.error(errorMessages?.camp?.patientUhidRequired)
      return
    }
    if (!visitId) {
      toast.error(errorMessages?.camp?.visitIdRequired)
      return
    }

    setIsSaving(true)
    try {
      // Get all selected test types
      const selectedTests = Object?.entries(selectedOptions)
        ?.filter(([isSelected]) => isSelected)
        ?.map(([testType]) => testType)

      // Filter out test types that have no files
      const testsWithFiles = selectedTests?.filter(
        (testType) => uploadedFiles[testType]?.length > 0
      )

      if (testsWithFiles.length === 0) {
        toast.info(errorMessages?.camp?.fileUpload?.selectAtLeastOneTestType)
        return
      }

      // Transform uploaded files into the required format
      const reportDocuments = testsWithFiles?.map((testType) => ({
        name: testType?.toUpperCase(),
        isNormal: true,
        comment: `${testType?.toUpperCase()} report`,
        urls: uploadedFiles[testType]
          ?.filter((file) => file?.url || file?.preview)
          ?.map((file) => file?.url || file?.preview || ''),
      }))

      const payload = {
        patientUhid,
        visitId,
        campId: selectedCamp?.campId,
        reportDocuments,
      }

      let response

      // If documentId exists, update the existing document, otherwise create a new one
      if (documentId) {
        response = await updateUploadAllDocuments(payload, documentId)
      } else {
        response = await uploadAllDocuments(payload)
      }

      if (response.success) {
        toast.success(
          `Documents ${documentId ? 'updated' : 'uploaded'} successfully`
        )

        setUploadedFiles((prevFiles) => ({
          ...prevFiles,
          ...Object?.fromEntries(
            testsWithFiles?.map((testType) => [testType, []])
          ),
        }))
        // Refresh the documents list to show all uploaded files
        await fetchDocuments()

        // Only clear the files that were just saved

        // Call the upload complete callback if provided
        onUploadComplete?.()
      } else {
        throw new Error(response?.message)
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error?.message
          : errorMessages?.camp?.fileUpload?.error
      toast.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  // Get test types with their labels
  const testTypes = Object?.entries(TEST_TYPES)?.map(([, value]) => ({
    key: value,
    label: errorMessages?.camp?.testTypeLabels[value as TestType],
  }))

  // Helper function to process server documents into our local format
  const processServerDocuments = useCallback(
    (documents: any[]): Record<TestType, Document[]> => {
      const updatedFiles: Record<TestType, Document[]> = {
        pft: [],
        ecg: [],
        pap: [],
      }

      documents?.forEach((doc) => {
        const docType = doc?.name?.toLowerCase() as TestType
        if (docType && Object?.values(TEST_TYPES)?.includes(docType)) {
          updatedFiles[docType] = (doc?.urls || [])?.map(
            (url: string, index: number) => ({
              docId: `saved-${docType}-${Date.now()}-${index}`,
              title: `${docType?.toUpperCase()} Document ${index + 1}`,
              url,
              testType: docType,
              fileId: url?.split('/').pop(),
            })
          )
        }
      })

      return updatedFiles
    },
    []
  )

  // Helper function to update document options based on server documents
  const updateDocumentOptions = useCallback(
    (documents: any[], currentOptions: any) => {
      const newOptions = { ...currentOptions }
      documents?.forEach((doc) => {
        const docType = doc?.name?.toLowerCase()
        if (docType && Object?.keys(currentOptions)?.includes(docType)) {
          newOptions[docType] = true
        }
      })
      return newOptions
    },
    []
  )

  // Helper function to process and merge files from server with existing files
  const processAndMergeFiles = useCallback((response: any, prevFiles: any) => {
    const newUploadedFiles = { ...prevFiles }
    const tempFiles = Object?.entries(prevFiles)?.reduce(
      (acc, [type, files]) => {
        acc[type] = (files as any[])?.filter((f) =>
          f?.docId?.startsWith('temp-')
        )
        return acc
      },
      {} as Record<string, any[]>
    )

    response?.data?.documents?.forEach((doc: any) => {
      const docType = doc?.name?.toLowerCase() as TestType
      if (doc?.files?.length && docType) {
        const existingFiles = (prevFiles[docType] || [])?.filter(
          (f: any) => !f?.docId?.startsWith('temp-')
        )

        const serverFiles = doc?.files?.map((file: any) => ({
          docId: doc?.id,
          fileId: file?.id,
          title: file?.originalName || doc?.name,
          url: file?.fileUrl,
          testType: docType,
        }))

        const filesMap = new Map()

        // Add existing files to map
        existingFiles?.forEach((file: Document) => {
          if (file?.fileId) filesMap?.set(file?.fileId, file)
        })

        // Add/overwrite with server files
        serverFiles?.forEach((file: Document) => {
          if (file?.fileId) filesMap?.set(file?.fileId, file)
        })

        // Merge and add temp files
        newUploadedFiles[docType] = [
          ...Array.from(filesMap?.values()),
          ...(tempFiles[docType] || []),
        ]?.filter(Boolean)
      }
    })

    return newUploadedFiles
  }, [])

  // Main fetch documents function
  const fetchDocuments = useCallback(async () => {
    if (!visitId) return

    setIsLoading(true)
    try {
      const response = await getAllDocuments(visitId)

      if (!response?.data) return

      // Save document ID if available
      if (response?.data?.id) {
        setDocumentId(response?.data?.id)
      }

      // Process documents if any
      if (response?.data?.documents?.length) {
        // Process server documents
        const updatedFiles = processServerDocuments(response?.data?.documents)

        // Update files state with server data
        setUploadedFiles((prevFiles) => ({
          ...updatedFiles,
          // Preserve non-document files
          ...Object?.fromEntries(
            Object?.entries(prevFiles)?.filter(
              ([key]) => !Object.values(TEST_TYPES)?.includes(key as TestType)
            )
          ),
        }))

        // Update selected options
        setSelectedOptions((prev) =>
          updateDocumentOptions(response?.data?.documents, prev)
        )

        // Process and merge files
        setUploadedFiles((prevFiles) =>
          processAndMergeFiles(response, prevFiles)
        )
      }
    } catch (error) {
      toast.error(errorMessages?.camp?.fileUpload?.error)
    } finally {
      setIsLoading(false)
    }
  }, [
    visitId,
    setIsLoading,
    setDocumentId,
    setSelectedOptions,
    setUploadedFiles,
    processServerDocuments,
    updateDocumentOptions,
    processAndMergeFiles,
  ])

  useEffect(() => {
    if (visitId) {
      fetchDocuments()
    }
  }, [visitId, fetchDocuments])

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      Object?.values(uploadedFiles)
        ?.flat()
        ?.forEach((file) => {
          if (file?.preview) {
            URL?.revokeObjectURL(file?.preview)
          }
        })
    }
  }, [uploadedFiles])

  return (
    <Box className={styles.container}>
      <Typography variant='h6' className={styles.title}>
        Upload Documents
      </Typography>

      <Box className={styles.testTypesContainer}>
        <Typography variant='subtitle1' className={styles.testTypesTitle}>
          Test Types
        </Typography>
        <Box className={styles.testTypesGrid}>
          {testTypes?.map(({ key, label }) => (
            <Box
              key={key}
              className={`${styles.testTypeOption} ${
                selectedOptions[key as keyof typeof selectedOptions]
                  ? styles.selected
                  : ''
              }`}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      selectedOptions[key as keyof typeof selectedOptions]
                    }
                    onChange={handleOptionChange}
                    name={key}
                    color='primary'
                  />
                }
                label={label}
                className={styles.formControlLabel}
              />

              {selectedOptions[key as keyof typeof selectedOptions] && (
                <Box className={styles.uploadContainer}>
                  <Button
                    variant='outlined'
                    component='label'
                    startIcon={<UploadFileIcon />}
                    className={styles.uploadButton}
                    fullWidth
                  >
                    <Typography variant='body2' color='textSecondary'>
                      Drag & drop files here or
                      <br />
                      click to browse
                    </Typography>
                    <input
                      type='file'
                      id={`file-upload-${key}`}
                      onChange={(e) => handleFileChange(e, key as TestType)}
                      className={styles.fileUploadInput}
                      multiple
                      hidden
                      accept='image/*,.pdf,.doc,.docx,.xls,.xlsx'
                    />
                  </Button>

                  {uploadedFiles[key as keyof typeof uploadedFiles]?.length >
                    0 && (
                    <Box className={styles.uploadedFiles}>
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        className={styles.uploadedFilesTitle}
                      >
                        Uploaded Files (
                        {
                          uploadedFiles[key as keyof typeof uploadedFiles]
                            ?.length
                        }
                        )
                      </Typography>
                      <Box className={styles.filesGrid}>
                        {uploadedFiles[key as keyof typeof uploadedFiles]?.map(
                          (file) => (
                            <Box key={file?.docId} className={styles.fileItem}>
                              <Typography
                                variant='body2'
                                className={styles.fileName}
                              >
                                {file?.title}
                              </Typography>
                              <Box className={styles.fileActions}>
                                {file?.preview && (
                                  <Tooltip
                                    title={
                                      errorMessages?.camp?.fileUploadUI
                                        ?.previewTooltip
                                    }
                                  >
                                    <IconButton
                                      size='small'
                                      onClick={() => handlePreview(file)}
                                      className={styles.iconButton}
                                    >
                                      <ZoomInOutlined fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip
                                  title={
                                    errorMessages?.camp?.fileUploadUI
                                      ?.downloadTooltip
                                  }
                                >
                                  <IconButton
                                    size='small'
                                    onClick={() =>
                                      handleDownload(file?.url, file?.title)
                                    }
                                    className={styles.iconButton}
                                  >
                                    <FileDownloadOutlined fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    errorMessages?.camp?.fileUploadUI
                                      ?.deleteTooltip
                                  }
                                >
                                  <IconButton
                                    size='small'
                                    onClick={() => {
                                      handleDeleteFile(
                                        file?.docId,
                                        key as TestType
                                      )
                                    }}
                                    color='error'
                                    className={styles.iconButton}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Modal
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        aria-labelledby='image-preview-modal'
        aria-describedby='image-preview'
        className={styles.modal}
      >
        <Box className={styles.modalContent}>
          <img
            src={selectedFile}
            alt='Preview'
            className={styles.previewImage}
          />
        </Box>
      </Modal>

      <Box className={styles.saveButtonContainer}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          startIcon={
            isSaving || isLoading ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <SaveIcon />
            )
          }
          disabled={isSaving || isLoading}
        >
          {isSaving || isLoading
            ? errorMessages?.camp?.fileUploadUI?.saveButtonSaving
            : errorMessages?.camp?.fileUploadUI?.saveButton}
        </Button>
      </Box>
    </Box>
  )
}

export default FileUploadWithOptions
